/**
 * UnconnectedUserBanner Component
 * Shows warning and actions when messaging someone you're not connected with
 * Updated with better error handling and unblock functionality
 */

import React, { useState } from 'react';
import { 
  AlertTriangle, 
  UserPlus, 
  Shield, 
  X, 
  Loader,
  CheckCircle,
  ShieldOff,
  Users
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import connectionService from '../../connect/services/connectionService';
import messagingService from '../services/messagingService';

/**
 * UnconnectedUserBanner Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.conversation - Conversation object
 * @param {Object} props.otherParticipant - Other participant info
 * @param {Function} props.onDismiss - Callback when banner is dismissed
 * @param {Function} props.onBlock - Callback when user is blocked
 * @param {Function} props.onUnblock - Callback when user is unblocked
 * @param {boolean} props.isBlocked - Whether the user is currently blocked
 * @returns {JSX.Element} Unconnected user banner component
 */
const UnconnectedUserBanner = ({ 
  conversation, 
  otherParticipant, 
  onDismiss, 
  onBlock,
  onUnblock,
  isBlocked = false
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [connectionSent, setConnectionSent] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Check if this is a message from an unconnected user to current user
  const isReceivingUnconnectedMessage = conversation.lastMessage?.isFromUnconnectedUser && 
    conversation.lastMessage?.senderId !== conversation.initiatedBy;

  // Handle sending a connection request
  const handleConnect = async () => {
    if (!otherParticipant?.id) return;
    
    try {
      setIsConnecting(true);
      
      // First check the current connection status
      const status = await connectionService.getConnectionStatus(
        conversation.participants.find(id => id !== otherParticipant.id),
        otherParticipant.id
      );
      
      if (status.status === 'connected') {
        toast.success('You are already connected with this user!');
        setConnectionStatus('connected');
        // Update conversation connection status
        await messagingService.updateConversationConnectionStatus(conversation.id, true);
        setTimeout(() => {
          handleDismiss();
        }, 1500);
        return;
      }
      
      if (status.status === 'pending') {
        if (status.direction === 'sent') {
          toast.info('Connection request already sent and pending');
        } else {
          toast.info('This user has already sent you a connection request. Check your pending requests.');
        }
        setConnectionStatus('pending');
        setTimeout(() => {
          handleDismiss();
        }, 2000);
        return;
      }
      
      await connectionService.sendConnectionRequest(
        otherParticipant.id, 
        `Hi! I'd like to connect with you.`
      );
      
      setConnectionSent(true);
      setConnectionStatus('sent');
      toast.success('Connection request sent!');
      
      // Auto-dismiss after showing success
      setTimeout(() => {
        handleDismiss();
      }, 2000);
      
    } catch (error) {
      console.error('Error sending connection request:', error);
      
      // Handle specific error messages
      if (error.message.includes('already connected')) {
        toast.success('You are already connected with this user!');
        setConnectionStatus('connected');
        // Update conversation connection status
        try {
          await messagingService.updateConversationConnectionStatus(conversation.id, true);
        } catch (updateError) {
          console.error('Error updating connection status:', updateError);
        }
      } else if (error.message.includes('already sent you a connection request')) {
        toast.info('This user has already sent you a connection request. Check your pending requests.');
        setConnectionStatus('pending_received');
      } else if (error.message.includes('already sent and pending')) {
        toast.info('Connection request already sent and pending');
        setConnectionStatus('pending_sent');
      } else if (error.message.includes('declined')) {
        toast.error('Your previous connection request was declined. Please wait before sending another request.');
      } else {
        toast.error(error.message || 'Failed to send connection request');
      }
      
      // Auto-dismiss after showing the message
      setTimeout(() => {
        handleDismiss();
      }, 3000);
      
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle blocking the user
  const handleBlock = async () => {
    if (!otherParticipant?.id || !conversation?.id) return;
    
    try {
      setIsBlocking(true);
      await messagingService.blockUserInConversation(conversation.id, otherParticipant.id);
      
      toast.success('User blocked successfully');
      onBlock && onBlock();
      
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    } finally {
      setIsBlocking(false);
    }
  };

  // Handle unblocking the user
  const handleUnblock = async () => {
    if (!conversation?.id) return;
    
    try {
      setIsUnblocking(true);
      await messagingService.unblockUserInConversation(conversation.id);
      
      toast.success('User unblocked successfully');
      onUnblock && onUnblock();
      
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error('Failed to unblock user');
    } finally {
      setIsUnblocking(false);
    }
  };

  // Handle dismissing the banner
  const handleDismiss = async () => {
    if (!conversation?.id) return;
    
    try {
      setIsDismissing(true);
      await messagingService.markUnconnectedMessageNotified(conversation.id);
      onDismiss && onDismiss();
    } catch (error) {
      console.error('Error dismissing banner:', error);
      onDismiss && onDismiss(); // Still dismiss on error
    } finally {
      setIsDismissing(false);
    }
  };

  // Show different states based on connection status
  if (connectionStatus === 'connected') {
    return (
      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4 mx-4">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-green-400 mr-3" />
          <div className="flex-1">
            <p className="text-sm text-green-700 font-medium">
              You are connected with {otherParticipant?.name || 'this user'}!
            </p>
            <p className="text-xs text-green-600 mt-1">
              You can now message each other freely.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (connectionSent || connectionStatus === 'sent') {
    return (
      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4 mx-4">
        <div className="flex items-center">
          <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
          <div className="flex-1">
            <p className="text-sm text-green-700 font-medium">
              Connection request sent to {otherParticipant?.name || 'this user'}!
            </p>
            <p className="text-xs text-green-600 mt-1">
              You'll be notified when they accept your request.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'pending_received') {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 mx-4">
        <div className="flex items-center">
          <UserPlus className="h-5 w-5 text-blue-400 mr-3" />
          <div className="flex-1">
            <p className="text-sm text-blue-700 font-medium">
              {otherParticipant?.name || 'This user'} has sent you a connection request
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Check your pending requests to accept or decline.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'pending_sent') {
    return (
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 mx-4">
        <div className="flex items-center">
          <CheckCircle className="h-5 w-5 text-blue-400 mr-3" />
          <div className="flex-1">
            <p className="text-sm text-blue-700 font-medium">
              Connection request pending
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Your connection request to {otherParticipant?.name || 'this user'} is still pending.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show unblock option if user is blocked
  if (isBlocked) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4">
        <div className="flex items-start">
          <Shield className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
          
          <div className="flex-1 min-w-0">
            <p className="text-sm text-red-700 font-medium mb-1">
              User is blocked
            </p>
            <p className="text-xs text-red-600 mb-3">
              You have blocked {otherParticipant?.name || 'this user'}. You won't receive messages from them.
            </p>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleUnblock}
                disabled={isUnblocking}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUnblocking ? (
                  <Loader className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <ShieldOff className="h-3 w-3 mr-1" />
                )}
                {isUnblocking ? 'Unblocking...' : 'Unblock User'}
              </button>
              
              <button
                onClick={handleDismiss}
                disabled={isDismissing}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDismissing ? (
                  <Loader className="h-3 w-3 animate-spin" />
                ) : (
                  'Dismiss'
                )}
              </button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            className="ml-2 flex-shrink-0 text-red-400 hover:text-red-500 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Default unconnected user warning
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 mx-4">
      <div className="flex items-start">
        <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          {isReceivingUnconnectedMessage ? (
            <>
              <p className="text-sm text-yellow-700 font-medium mb-1">
                Message from someone not in your connections
              </p>
              <p className="text-xs text-yellow-600 mb-3">
                <span className="font-medium">{otherParticipant?.name || 'This user'}</span> isn't 
                in your connections. Be cautious when sharing personal information.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-yellow-700 font-medium mb-1">
                You're messaging someone not in your connections
              </p>
              <p className="text-xs text-yellow-600 mb-3">
                Connect with <span className="font-medium">{otherParticipant?.name || 'this user'}</span> to 
                build trust and have better conversations.
              </p>
            </>
          )}
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleConnect}
              disabled={isConnecting}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? (
                <Loader className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <UserPlus className="h-3 w-3 mr-1" />
              )}
              {isConnecting ? 'Connecting...' : 'Send Connection Request'}
            </button>
            
            <button
              onClick={handleBlock}
              disabled={isBlocking}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBlocking ? (
                <Loader className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Shield className="h-3 w-3 mr-1" />
              )}
              {isBlocking ? 'Blocking...' : 'Block User'}
            </button>
            
            <button
              onClick={handleDismiss}
              disabled={isDismissing}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDismissing ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : (
                'Dismiss'
              )}
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          disabled={isDismissing}
          className="ml-2 flex-shrink-0 text-yellow-400 hover:text-yellow-500 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default UnconnectedUserBanner;